import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import {
  EuroOutlined,
  GroupsOutlined,
  HandshakeOutlined,
  LocalOfferOutlined,
  LocalShippingOutlined,
  ReceiptLongOutlined,
  ArticleOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { FunctionComponent, SVGProps } from 'react';
import { AuthorizationObjectPermissions, AuthorizationObjectType } from '@kheops/graphql';
import { RoutePaths } from '../../routes/AppRoutes';
import { GlobalAuthorization } from '../../state';

export type NavigationAction = 'logout' | 'help';

export interface SubNavigationTabData {
  to: string;
  label: string;
  authorization?: GlobalAuthorization;
}

export interface NavigationTabData extends SubNavigationTabData {
  category: NavigationCategories;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
  subTabs: SubNavigationTabData[];
}

export enum NavigationCategories {
  PROFILE = 'PROFILE',
  PRODUCTS = 'PRODUCTS',
  PRICES = 'PRICES',
  DELIVERY = 'DELIVERY',
  BILLING = 'BILLING',
  CATALOGS = 'CATALOGS',
  TEAM = 'TEAM',
  SERVICES = 'SERVICES',
}

export interface NavigationItem {
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
  labelKey: string;
  // to and action should not be set at the same time, to is used in that case
  to?: string;
  action?: NavigationAction;
  desktopOnly?: boolean;
  mobileOnly?: boolean;
  authorization?: GlobalAuthorization;
}

export const SupplierNavigation: NavigationItem[] = [
  {
    icon: StorefrontOutlined,
    labelKey: 'settings:company',
    to: RoutePaths.COMPANY_PROFILE,
  },
  {
    icon: HandshakeOutlined,
    labelKey: 'settings:services',
    to: RoutePaths.COMPANY_SERVICES,
  },
  {
    icon: LocalOfferOutlined,
    labelKey: 'common:products',
    to: RoutePaths.COMPANY_PRODUCTS,
  },
  {
    icon: ArticleOutlined,
    labelKey: 'catalogs:catalogs',
    to: RoutePaths.COMPANY_CATALOGS,
  },
  {
    icon: EuroOutlined,
    labelKey: 'common:prices',
    to: RoutePaths.COMPANY_PRODUCTS_PRICES,
    desktopOnly: true,
  },
  {
    icon: EuroOutlined,
    labelKey: 'common:prices',
    to: RoutePaths.NAVIGATION_PRICES,
    mobileOnly: true,
  },
  {
    icon: LocalShippingOutlined,
    labelKey: 'common:delivery',
    to: RoutePaths.COMPANY_DELIVERY_ZONES,
  },
  {
    icon: GroupsOutlined,
    labelKey: 'common:team',
    to: RoutePaths.COMPANY_TEAM,
    authorization: {
      object: AuthorizationObjectType.Company,
      permission: AuthorizationObjectPermissions.CanEditRole,
    },
  },
];

export const BuyerNavigation: NavigationItem[] = [
  {
    icon: LocalShippingOutlined,
    labelKey: 'common:delivery',
    to: RoutePaths.COMPANY_BUYER_DELIVERY_INFO,
  },
  {
    icon: ReceiptLongOutlined,
    labelKey: 'common:billing',
    to: RoutePaths.COMPANY_BUYER_BILLING_INFO,
  },
  {
    icon: GroupsOutlined,
    labelKey: 'common:team',
    to: RoutePaths.COMPANY_TEAM,
    authorization: {
      object: AuthorizationObjectType.Company,
      permission: AuthorizationObjectPermissions.CanEditRole,
    },
  },
  {
    icon: FavoriteBorderOutlinedIcon,
    labelKey: 'wishlist:navigation_label',
    to: RoutePaths.WISHLIST,
    mobileOnly: true,
    authorization: {
      object: AuthorizationObjectType.Company,
      permission: AuthorizationObjectPermissions.CanAccessWishlist,
    },
  },
];

export const CommonNavigation: NavigationItem[] = [
  {
    icon: PersonOutlinedIcon,
    labelKey: 'common:personal_information',
    to: RoutePaths.USER,
  },
  {
    icon: LogoutOutlinedIcon,
    labelKey: 'common:sign_out',
    action: 'logout',
  },
];

export const MobileNavigation: NavigationItem[] = [
  {
    icon: PersonOutlinedIcon,
    labelKey: 'common:personal_information',
    to: RoutePaths.USER,
  },
  {
    icon: HelpOutlineOutlinedIcon,
    labelKey: 'common:help_center',
    action: 'help',
  },
  {
    icon: ChangeCircleOutlinedIcon,
    labelKey: 'common:switch_company',
    to: RoutePaths.NAVIGATION_CONTEXTS,
  },
];

export const PricesNavigation: NavigationTabData = {
  category: NavigationCategories.PRICES,
  to: RoutePaths.COMPANY_PRODUCTS_PRICES,
  icon: EuroOutlined,
  label: 'common:prices',
  subTabs: [
    {
      to: RoutePaths.COMPANY_PRODUCTS_PRICES,
      label: 'contracts:default_price_and_srp',
    },
    {
      to: RoutePaths.COMPANY_GLOBAL_DISCOUNTS,
      label: 'price:global_discounts',
    },
    {
      to: RoutePaths.COMPANY_CUSTOM_PRICE_LISTS,
      label: 'price:custom_price_lists',
    },
  ],
};

export const SupplierCompanyNavMap: NavigationTabData[] = [
  {
    category: NavigationCategories.PROFILE,
    to: RoutePaths.COMPANY_PROFILE,
    icon: StorefrontOutlined,
    label: 'settings:company',
    subTabs: [],
  },
  {
    category: NavigationCategories.SERVICES,
    to: RoutePaths.COMPANY_SERVICES,
    icon: HandshakeOutlined,
    label: 'settings:services',
    subTabs: [],
  },
  {
    category: NavigationCategories.PRODUCTS,
    to: RoutePaths.COMPANY_PRODUCTS,
    icon: LocalOfferOutlined,
    label: 'common:product_other',
    subTabs: [],
  },
  {
    category: NavigationCategories.CATALOGS,
    to: RoutePaths.COMPANY_CATALOGS,
    icon: ArticleOutlined,
    label: 'catalogs:catalogs',
    subTabs: [],
  },
  {
    ...PricesNavigation
  },
  {
    category: NavigationCategories.DELIVERY,
    to: RoutePaths.COMPANY_DELIVERY_ZONES,
    icon: LocalShippingOutlined,
    label: 'common:delivery',
    subTabs: [],
  },
  {
    category: NavigationCategories.TEAM,
    to: RoutePaths.COMPANY_TEAM,
    icon: GroupsOutlined,
    label: 'common:team',
    subTabs: [],
    authorization: {
      object: AuthorizationObjectType.Company,
      permission: AuthorizationObjectPermissions.CanEditRole,
    },
  },
];

export const BuyerCompanyNavMap: NavigationTabData[] = [
  {
    category: NavigationCategories.DELIVERY,
    to: RoutePaths.COMPANY_BUYER_DELIVERY_INFO,
    icon: LocalShippingOutlined,
    label: 'common:delivery',
    subTabs: [],
  },
  {
    category: NavigationCategories.BILLING,
    to: RoutePaths.COMPANY_BUYER_BILLING_INFO,
    icon: ReceiptLongOutlined,
    label: 'common:billing',
    subTabs: [],
  },
  {
    category: NavigationCategories.TEAM,
    to: RoutePaths.COMPANY_TEAM,
    icon: GroupsOutlined,
    label: 'common:team',
    subTabs: [],
    authorization: {
      object: AuthorizationObjectType.Company,
      permission: AuthorizationObjectPermissions.CanEditRole,
    },
  },
];
